import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useMigrationAppliancesTable } from './hooks';
import { createTableColumns } from './tableConfig/tableColumns';
import { MigrationApplianceDetailsDrawer } from './MigrationApplianceDetailsDrawer';
import MigrationAppliancesAddTagsModal from '../../components/modals/MigrationAppliancesAddTagsModal';
import { DeleteModal, Table, Card, Typography } from '../../components';
import NetworkTroubleshootingModal from './NetworkTroubleshootingModal';

import './MigrationAppliancesTable.scss';

const { Text } = Typography;

export const MigrationAppliancesTable = ({
    selectedRowKeys,
    onSelectedRowKeysChange,
    bulkDeleteTriggerRef,
    bulkAddTagsTriggerRef,
    editCloudAccountTriggerRef,
    redeployApplianceTriggerRef,
    onBulkActionSuccess,
}) => {
    const [tagsModalOpen, setTagsModalOpen] = useState(false);
    const [applianceName, setApplianceName] = useState('');
    const [applianceTags, setApplianceTags] = useState([]);
    const [applianceId, setApplianceId] = useState('');

    const {
        // Loading states
        isLoading,
        isFetching,
        isDeleting,
        isBulkDeleting,

        // data for table
        tableData,

        // state
        pagination,
        drawerOpen,
        selectedApplianceId,
        selectedCloudAccountName,
        deleteModalOpen,
        bulkDeleteModalOpen,
        appliancesToDelete,

        // Network Troubleshooting Modal state
        troubleshootingModalOpen,
        selectedTroubleshootingApplianceId,
        selectedTroubleshootingApplianceName,

        // handlers
        handleRowClick,
        handlePaginationChange,
        handleOpenDeleteModal,
        handleUpgradeAppliance,
        handleOpenTroubleshootingModal,
        handleCloseTroubleshootingModal,
        handleDeleteCloudAccount,
        recordToDelete,
        handleBulkDelete,
        handleBulkDeleteConfirm,
        handleBulkDeleteCancel,
        onSelectChange,
        refetch,

        // Configuration
        rowSelection,
        hasDetailsDrawer,

        // State setters
        setSelectedApplianceId,
        setDrawerOpen,
        setDeleteModalOpen,
    } = useMigrationAppliancesTable({
        selectedRowKeys,
        onSelectedRowKeysChange,
        bulkDeleteTriggerRef,
        bulkAddTagsTriggerRef,
        onBulkActionSuccess,
    });

    const handleEditCloudAccount = useCallback(
        (cloudAccountId) => {
            if (editCloudAccountTriggerRef && editCloudAccountTriggerRef.current) {
                editCloudAccountTriggerRef.current(cloudAccountId);
            }
        },
        [editCloudAccountTriggerRef],
    );

    const handleRedeployAppliance = useCallback(
        (cloudAccountId) => {
            if (redeployApplianceTriggerRef && redeployApplianceTriggerRef.current) {
                redeployApplianceTriggerRef.current(cloudAccountId);
            }
        },
        [redeployApplianceTriggerRef],
    );

    const columns = createTableColumns({
        handleRowClick,
        handleOpenDeleteModal,
        handleUpgradeAppliance,
        handleRedeployAppliance,
        handleOpenTroubleshootingModal,
        handleEditCloudAccount,
    });

    useEffect(() => {
        if (bulkDeleteTriggerRef) {
            bulkDeleteTriggerRef.current = handleBulkDelete;
        }
    }, [bulkDeleteTriggerRef, handleBulkDelete]);

    // Use the controlled row selection from props
    const tableRowSelection = useMemo(
        () => ({
            ...rowSelection,
            selectedRowKeys: selectedRowKeys || [],
            onChange: onSelectedRowKeysChange || (() => {}),
        }),
        [rowSelection, selectedRowKeys, onSelectedRowKeysChange],
    );

    // Handle row click
    const onRow = (record) => ({
        onClick: (e) => {
            // If clicking in the tags column, open the TagsModal and not the drawer
            if (e.target.closest('.tags-column')) {
                e.stopPropagation();
                setTagsModalOpen(true);
                setApplianceName(record.name);
                setApplianceTags(record.tags || []);
                setApplianceId(record.id);
                return;
            } else {
                handleRowClick(e, record);
            }
        },
        style: hasDetailsDrawer(record) ? { cursor: 'pointer' } : {},
    });

    return (
        <div className="migration-appliances-table-container">
            <Card>
                <Table
                    dataSource={tableData}
                    onRow={onRow}
                    loading={isLoading || isFetching}
                    rowKey="id"
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: [10, 25, 50, 100],
                        onChange: handlePaginationChange,
                        onShowSizeChange: handlePaginationChange,
                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                    }}
                    columns={columns}
                    rowSelection={tableRowSelection}
                    locale={{
                        emptyText: !(isLoading || isFetching) ? (
                            <div>
                                <Text>
                                    No Migration Appliances have been added. For instructions, see{' '}
                                    <a
                                        href="http://docs.rivermeadow.com/getting-started"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Getting Started
                                    </a>
                                    .
                                </Text>
                            </div>
                        ) : undefined,
                    }}
                />
            </Card>

            <MigrationApplianceDetailsDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                applianceId={selectedApplianceId}
                // userOrg={userOrg}
            />
            <MigrationAppliancesAddTagsModal
                open={tagsModalOpen}
                handleCancel={() => setTagsModalOpen(false)}
                onClose={() => setTagsModalOpen(false)}
                applianceName={applianceName}
                initialTags={applianceTags}
                applianceId={applianceId}
                onSuccess={() => {
                    setTagsModalOpen(false);
                    refetch();
                }}
            />

            <NetworkTroubleshootingModal
                open={troubleshootingModalOpen}
                onClose={handleCloseTroubleshootingModal}
                applianceId={selectedTroubleshootingApplianceId}
                applianceName={selectedTroubleshootingApplianceName}
            />

            {/* Single Cloud Account Delete Modal */}
            <DeleteModal
                open={deleteModalOpen}
                title="Remove Cloud Target Account"
                message={`Are you sure you want to remove "${selectedCloudAccountName}"?`}
                warningMessage="Once removed, this cloud account cannot be recovered. Any associated migration appliances will also be removed."
                onCancel={() => {
                    setDeleteModalOpen(false);
                }}
                onConfirm={() => {
                    handleDeleteCloudAccount();
                }}
                confirmLoading={isDeleting}
            />
            {/* Bulk Cloud Accounts Delete Modal */}
            <DeleteModal
                open={bulkDeleteModalOpen}
                title="Confirm"
                message={`Are you sure you want to delete ${appliancesToDelete.length} cloud account${appliancesToDelete.length > 1 ? 's' : ''}?`}
                warningMessage="Once deleted, it cannot be recovered."
                onCancel={handleBulkDeleteCancel}
                onConfirm={handleBulkDeleteConfirm}
                confirmLoading={isBulkDeleting}
            />
        </div>
    );
};
